/**
 * YearlyGrowth - Travel growth analytics with charts
 * Duo = basic charts, Pro = projections + comparisons
 */
import { useState, useEffect } from "react";
import { 
  TrendingUp, Calendar, Globe, MapPin, BarChart3, 
  Lock, Sparkles, ChevronRight, Sun 
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { useEntitlements } from "@/hooks/useEntitlements";
import { PaywallScreen, PaywallType } from "@/components/PaywallScreen";

const YearlyGrowth = () => {
  const { hasUniversalAccess, hasPaidAccess, hasDuoAccess } = useEntitlements();
  const hasBasicAccess = hasUniversalAccess || hasDuoAccess || hasPaidAccess;
  const hasProAccess = hasUniversalAccess || hasPaidAccess;
  
  const [loading, setLoading] = useState(true);
  const [growthData, setGrowthData] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  
  useEffect(() => {
    fetchGrowthData();
  }, []);
  
  const fetchGrowthData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/premium/growth`);
      setGrowthData(response.data);
    } catch (error) {
      console.error("Failed to fetch growth data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <Card className="bg-charcoal/95 border-coral/20">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral"></div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Get max value for chart scaling
  const maxCountries = Math.max(...(growthData?.yearly_data?.map(y => y.countries_visited) || [1]));
  
  return (
    <>
      <Card className="bg-charcoal/95 border-coral/20 backdrop-blur-xl">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-white">
              <TrendingUp className="w-5 h-5 text-coral" />
              Yearly Travel Growth
            </CardTitle>
            {!hasBasicAccess && (
              <Badge variant="outline" className="border-coral/50 text-coral text-xs">
                <Lock className="w-3 h-3 mr-1" />
                Duo+
              </Badge>
            )}
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Summary Stats */}
          {growthData?.cumulative_countries !== undefined && (
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white/5 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-coral">{growthData.cumulative_countries}</p>
                <p className="text-xs text-white/60">Total Countries</p>
              </div>
              <div className="bg-white/5 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-emerald-400">{growthData.total_years_traveled}</p>
                <p className="text-xs text-white/60">Years Active</p>
              </div>
              <div className="bg-white/5 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-amber-400">{growthData.best_year?.new_countries || 0}</p>
                <p className="text-xs text-white/60">Best Year</p>
              </div>
            </div>
          )}
          
          {/* Yearly Bar Chart */}
          {hasBasicAccess && growthData?.yearly_data?.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-white/80 flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Countries Per Year
              </h4>
              <div className="space-y-2">
                {growthData.yearly_data.slice(-5).map((yearData, idx) => (
                  <div key={yearData.year} className="flex items-center gap-3">
                    <span className="text-xs text-white/60 w-12">{yearData.year}</span>
                    <div className="flex-1 h-6 bg-white/5 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-coral to-coral/60 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                        style={{ width: `${(yearData.countries_visited / maxCountries) * 100}%` }}
                      >
                        <span className="text-xs font-medium text-white">{yearData.countries_visited}</span>
                      </div>
                    </div>
                    {yearData.new_countries > 0 && (
                      <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">
                        +{yearData.new_countries} new
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Pro Features: Projection & Monthly Activity */}
          {hasProAccess && growthData?.projection && (
            <div className="bg-coral/10 rounded-lg p-4 border border-coral/20">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-coral" />
                <h4 className="text-sm font-medium text-white">This Year's Projection</h4>
              </div>
              {growthData.projection.projected_trips ? (
                <p className="text-white/80 text-sm">
                  On track for <span className="font-bold text-coral">{growthData.projection.projected_trips}</span> trips 
                  {growthData.projection.projected_new_countries > 0 && (
                    <> and <span className="font-bold text-emerald-400">{growthData.projection.projected_new_countries}</span> new countries</>
                  )}
                </p>
              ) : (
                <p className="text-white/60 text-sm">
                  {growthData.projection.basis || "Add more dated trips this year for a reliable forecast"}
                </p>
              )}
              {growthData.projection.projected_trips && (
                <Badge className="mt-2 bg-white/10 text-white/60 text-xs">
                  {growthData.projection.confidence} confidence
                </Badge>
              )}
            </div>
          )}
          
          {hasProAccess && growthData?.monthly_activity && (
            <div className="bg-white/5 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sun className="w-4 h-4 text-amber-400" />
                <h4 className="text-sm font-medium text-white">Peak Travel Month</h4>
              </div>
              <p className="text-white/80">
                <span className="font-bold text-amber-400">{growthData.monthly_activity.most_active_month}</span>
                <span className="text-white/60 text-sm ml-2">
                  ({growthData.monthly_activity.trips_in_month} trips)
                </span>
              </p>
            </div>
          )}
          
          {/* Locked Pro features for Duo users */}
          {hasBasicAccess && !hasProAccess && (
            <div className="space-y-2">
              <div 
                className="bg-white/5 rounded-lg p-3 flex items-center justify-between cursor-pointer hover:bg-white/10 transition-colors"
                onClick={() => setShowPaywall(true)}
              >
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-white/40" />
                  <span className="text-sm text-white/60">Year Projection</span>
                </div>
                <Badge variant="outline" className="border-coral/50 text-coral text-xs">Pro</Badge>
              </div>
              <div 
                className="bg-white/5 rounded-lg p-3 flex items-center justify-between cursor-pointer hover:bg-white/10 transition-colors"
                onClick={() => setShowPaywall(true)}
              >
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-white/40" />
                  <span className="text-sm text-white/60">Monthly Analysis</span>
                </div>
                <Badge variant="outline" className="border-coral/50 text-coral text-xs">Pro</Badge>
              </div>
            </div>
          )}
          
          {/* Teaser for free users */}
          {!hasBasicAccess && growthData?.teaser && (
            <div className="space-y-3">
              <div className="text-center py-4">
                <p className="text-white/60 text-sm mb-2">
                  {growthData.teaser.total_countries} countries across {growthData.teaser.total_years} years
                </p>
                <p className="text-xs text-white/40">{growthData.teaser.message}</p>
              </div>
              <Button
                onClick={() => setShowPaywall(true)}
                className="w-full bg-coral hover:bg-coral/90"
              >
                <Lock className="w-4 h-4 mr-2" />
                Learn more on duolyst.co.uk
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {showPaywall && (
        <PaywallScreen
          isOpen={showPaywall}
          onClose={() => setShowPaywall(false)}
          type={PaywallType.FEATURE_GATE}
          feature="yearly_growth"
          title="Yearly Travel Growth"
          description="Track your travel progression over time."
        />
      )}
    </>
  );
};

export default YearlyGrowth;
